import type { ReactElement, ReactNode } from 'react';

import './EmptyState.css';

interface EmptyStateProps {
  /** The title. One line, says what is not here rather than what went wrong. */
  heading: string;
  /** The body. A node rather than a string so a sentence can carry a link. */
  body: ReactNode;
  /**
   * Exactly one action. Required: an empty surface with no way off it is the
   * dead end this redesign exists to remove. A second route onward belongs in
   * the body as a link, never as a second button.
   */
  action: ReactElement;
  /**
   * Supplementary content between body and action — the list of checks that
   * ran, say. Never a second action.
   */
  detail?: ReactNode;
  className?: string;
}

/**
 * Every empty surface in the application. Icon-free by design: a title, a
 * body, and one action.
 *
 * Separated by a hairline and space rather than boxed — VISUAL.md reserves
 * the bordered block for the receipt and the document pane.
 */
export default function EmptyState({
  heading,
  body,
  action,
  detail,
  className,
}: EmptyStateProps) {
  return (
    <div className={['empty', className].filter(Boolean).join(' ')}>
      <h2 className="empty__heading">{heading}</h2>
      <div className="empty__body">{body}</div>
      {detail ? <div className="empty__detail">{detail}</div> : null}
      <div className="empty__action">{action}</div>
    </div>
  );
}
